import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { FormGroup, Validators } from "@angular/forms";
import {
  AlertController,
  Events,
  LoadingController,
  ToastController,
} from "ionic-angular";
import * as moment from "moment-timezone";
import { commonVar } from "../common-var/common";

@Injectable()
export class UtilityService {
  public spinnerCounter: number = 0;
  spinner = null;
  serverDateFormat: string = "YYYY-MM-DDTHH:mm:ss.SSS";
  constructor(
    public http: HttpClient,
    public toastCtrl: ToastController,
    public loadingCtrl: LoadingController,
    public alertCtrl: AlertController,
    public events: Events
  ) {}

  convertDateToServerFormat(
    dateAndTime: string,
    dateFormat: string = this.serverDateFormat
  ) {
    let convertedDate = moment(dateAndTime).utc().format(dateFormat);
    return convertedDate === "Invalid date" ? null : convertedDate;
  }

  covertDatetoLocalFormat(
    inputDate: string,
    dateFormat: string = this.serverDateFormat
  ) {
    let convertedDate = moment
      .utc(inputDate, dateFormat)
      .local()
      .format(dateFormat);
    return convertedDate === "Invalid date" ? "" : convertedDate;
  }

  /**
   * Converts a UTC date to a local date based on the offset.
   *
   * @param utcDatetime - the UTC date to be converted (i.e. '2021-06-23T05:00:00Z')
   * @param offset - the number of seconds to offset the utcDatetime (i.e. -18000, which equates to -300 mins = -05:00 hours = CDT)
   * @param dateFormat - preferred return date format (default is 'YYYY-MM-DDTHH:mm:ss.SSS' if parameter not provided)
   * @returns - the converted date based on the offset provided.
   */
  convertUtcDateUsingOffset(
    utcDatetime: string,
    offset,
    dateFormat: string = this.serverDateFormat
  ) {
    utcDatetime = moment.utc(utcDatetime);

    // convert using offset if provided, otherwise use browser timezone
    let convertedDate = offset
      ? moment(utcDatetime).utcOffset(offset / 60)
      : moment(utcDatetime).local();

    return moment(convertedDate).isValid()
      ? moment(convertedDate).format(dateFormat)
      : null;
  }

  /**
   * Sets the provided date in the given timezone.
   * @param datetime - the date to be set (i.e. '2021-06-23T15:10:00')
   * @param timezone - the timezone to set the date in (i.e. America/Chicago)
   * @param isStartOfDay - flag to set the HH:mm:ss to 00:00:00
   * @returns - the date now set in the given timezone (i.e. '2021-06-23T00:00:00-05:00')
   */
  setDateInTimeZone(
    datetime: string,
    timezone: string,
    isStartOfDay: boolean = false
  ) {
    if (!datetime || !timezone) {
      return datetime;
    }

    datetime = moment(datetime).format("YYYY-MM-DDTHH:mm:ss");

    // 1. Set default timezone to the Org's timezone
    moment.tz.setDefault(timezone);

    // 2. create moment instance based on the timezone provided
    let convertedDatetime = moment(datetime).format();

    // 3. Set HH:mm:ss = 00:00:00 if setting to start of day
    convertedDatetime = isStartOfDay
      ? moment(convertedDatetime).startOf("day")
      : moment(convertedDatetime);

    // set default timezone back to browser
    moment.tz.setDefault();

    return convertedDatetime.format();
  }

  /**
   * Gets the offset for a given datetime.
   * @param datetime - the date to get the offset for (i.e. '2021-06-23T00:00:00-05:00')
   * @returns
   */
  getOffsetInSecondsForDate(datetime: string): number {
    return moment.parseZone(datetime).utcOffset() * 60;
  }

  getTimeZone() {
    let timeZone = moment.tz.guess();
    let time = new Date();
    return moment(time).tz(timeZone).format("z");
  }

  formatDate(date: Date, format: string) {
    return date == null ? null : moment(date).format(format);
  }

  /**
   * Show error message
   * @param err
   * @param message
   */
  presentErrorToast(err: HttpErrorResponse, message: string) {
    if (err && (err as any).code === "REFRESH_TOKEN_FAILED") {
      return;
    }
    message += " ";
    if (err.error instanceof Error) {
      message += "Client error.";
    } else {
      message += " ";
      if (err.status == 401) {
        message += "User not authorized.";
      } else if (err.status >= 500) {
        // Do not modify message
      } else {
        message += "Communication error.";
      }
    }

    let toast = this.toastCtrl.create({
      message: message,
      duration: 6000,
      position: "below",
      cssClass: "error",
    });

    toast.present();
  }

  presentCustomErrorToast(message: string) {
    let toast = this.toastCtrl.create({
      message: message,
      duration: 6000,
      position: "below",
      cssClass: "error",
    });

    toast.present();
  }

  presentCustomWarningToast(message: string) {
    let toast = this.toastCtrl.create({
      message: message,
      duration: 6000,
      position: "below",
      cssClass: "warning",
    });

    toast.present();
  }

  presentSuccessToast(message: string) {
    let toast = this.toastCtrl.create({
      message: message,
      duration: 4000,
      position: "below",
      cssClass: "success",
    });

    toast.present();
  }

  public increaseSpinnerCount(message: string = "Loading...") {
    this.spinnerCounter++;
    if (this.spinnerCounter && this.spinner == null) {
      this.spinner = this.loadingCtrl.create({
        content: message,
      });
      this.spinner.present();
    }
  }

  public decreaseSpinnerCount() {
    this.spinnerCounter--;
    if (this.spinnerCounter <= 0 && this.spinner) {
      this.spinner.dismiss();
      this.spinner = null;
    }
  }

  public confirmationPopUp(title, text, message, data, typeOfEvent) {
    let alert = this.alertCtrl.create({
      title: title,
      message: message,
      buttons: [
        {
          text: "Cancel",
          role: "cancel",
        },
        {
          text: text,
          handler: () => {
            this.events.publish(typeOfEvent, data);
          },
        },
      ],
    });
    alert.present();
  }

  public download(base64Data, fileName) {
    const a = document.createElement("a");
    document.body.appendChild(a);
    a.href = base64Data;
    a.setAttribute("download", fileName);
    a.target = "_blank";
    const event = document.createEvent("MouseEvents");
    event.initEvent("click", false, true);
    a.dispatchEvent(event);
    a.remove();
  }

  convertDateTimeWithTimezone(date, timezone) {
    const options = {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
    };

    const localDate = date.toLocaleString("en-US", options);
    const splitDateTime = localDate.split(",");
    if (splitDateTime.length == 2) {
      const splitDate = splitDateTime[0].split("/");
      return splitDate[2] + "-" + splitDate[0] + "-" + splitDate[1];
    }
  }

  public isDynamicGlobalHeader(page: string, type: string): boolean {
    switch (type) {
      case "assessmentType": {
        return commonVar.PAGES_ALLOWED_FOR_GLOBAL_HEADER_CHANGE.assessmentType.includes(
          page ? page.toLowerCase() : ""
        );
      }
      case "currentPage": {
        return commonVar.PAGES_ALLOWED_FOR_GLOBAL_HEADER_CHANGE.currentPage.includes(
          page ? page.toLowerCase() : ""
        );
      }
      case "sectionId": {
        return commonVar.PAGES_ALLOWED_FOR_GLOBAL_HEADER_CHANGE.sectionId.includes(
          page ? page.toLowerCase() : ""
        );
      }
      default: {
        return false;
      }
    }
  }

  uuid() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      let r = (Math.random() * 16) | 0,
        v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  validateOtherCredentials($event: any, physicianForm: FormGroup) {
    if ($event === "Other" && physicianForm.contains("credentialsInputvalue")) {
      physicianForm.controls["credentialsInputvalue"].setValidators([
        Validators.required,
        Validators.minLength(2),
      ]);
      physicianForm.controls["credentialsInputvalue"].updateValueAndValidity();
    } else {
      physicianForm.controls["credentialsInputvalue"].clearValidators();
    }
  }
}



// WEBPACK FOOTER //
// ./src/providers/utility-service/utility-service.ts